/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { NumberOfHits } from "@/components/atoms/product-page/hits";
import ProductCard from "@/components/molecules/product-card";
import { Locales } from "@/lib/i18n";
import { initMeiliSearch } from "@/lib/meilisearch";
import { resolveProductConditioning } from "@/lib/product-units";
import { segments } from "@/locales/segment";
import { TagDocument } from "@/prismicio-types";
import { useEffect, useState } from "react";
import { Configure, Hits, InstantSearch, Pagination } from "react-instantsearch";

interface ProductsTagProps {
  tag: TagDocument;
  locale: Locales;
}

const SafeConfigure = Configure as React.ComponentType<any>;

const ProductsTag = ({ tag, locale }: ProductsTagProps) => {
  const [searchClient, setSearchClient] = useState<any>(null);
  const [isClient, setIsClient] = useState<boolean>(false);

  const indexName = `products_${locale}`;

  useEffect(() => {
    try {
      const { searchClient: client } = initMeiliSearch();
      setSearchClient(client);
      setIsClient(true);
    } catch (error) {
      console.error("Erreur lors de l'initialisation de MeiliSearch:", error);
    }
  }, []);

  if (!isClient || !searchClient) {
    return (
      <>
        <div className="grid w-full grid-cols-1 gap-x-5 py-10 lg:grid-cols-[380px_1fr]">
          <div className="row-start-1 mb-6 h-6 w-full rounded bg-gray-200 lg:col-start-2"></div>
          <aside className="col-start-1 row-start-2 w-full max-w-[380px] flex-shrink-0">
            <div className="h-64 animate-pulse rounded bg-gray-200"></div>
          </aside>
          <div className="w-full lg:col-start-2 lg:row-start-2">
            <div className="animate-pulse">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                <div className="h-6 w-full rounded bg-gray-200"></div>
                <div className="h-6 w-full rounded bg-gray-200"></div>
                <div className="h-6 w-full rounded bg-gray-200"></div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const Hit = ({ hit }: { hit: any }) => {
    const lastLevel = Object?.keys(hit?.categories || {})?.sort((a, b) => {
      // extraire les numéros (lvl0 -> 0, lvl3 -> 3)
      return parseInt(b.replace("lvl", "")) - parseInt(a.replace("lvl", ""));
    })[0];

    const lastCategoryFull = hit.categories?.[lastLevel];

    // Extraire uniquement la dernière sous-catégorie (la plus spécifique)
    const lastCategory = Array.isArray(lastCategoryFull)
      ? lastCategoryFull?.[0]?.split(" > ").pop()
      : lastCategoryFull?.split(" > ").pop();

    return (
      <ProductCard
        locale={locale}
        key={hit.id}
        productId={hit.slug}
        medusaId={hit?.medusaId || hit?.id}
        medusaVariantId={hit.medusaVariantId}
        productName={hit.title}
        isOutOfStock={hit?.status ? (hit.status === "draft" ? true : false) : false}
        productPrice={hit.price}
        productImage={hit.featuredImage ? hit.featuredImage : hit.images?.[0] ? hit.images[0] : ""}
        productTag={lastCategory}
        reference={hit.sku}
        link={`${locale != "fr" ? "/" + locale : ""}/${segments[locale || "fr"].product}/${hit.slug}`}
        isCollection={hit.isCollection}
        productConditioning={resolveProductConditioning(hit.conditioning, hit.conditioning_bac)}
        variant={hit.image_full_size_card ? "image-only" : "default"}
        isRange={hit?.is_surface_based ? true : false}
        isPriceDynamic={hit?.is_price_dynamic ? true : false}
        forSale={hit?.product_type ? (hit?.product_type === "Sale Product" ? true : false) : false}
      />
    );
  };

  return (
    <InstantSearch indexName={indexName} searchClient={searchClient} routing={true}>
      <SafeConfigure
        hitsPerPage={12}
        //   facetFilters={[`categories.lvl${currentLvl}:${categoryPath}`]}
        facetFilters={[`tags:${tag.data.title}`]}
        sort={["products_fr:display:asc"]}
      />

      <NumberOfHits locale={locale} />

      <div className="grid-tag grid w-full grid-cols-1 gap-x-5 py-10">
        <Hits hitComponent={Hit} />
        <div className="mt-6">
          <Pagination
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      </div>
    </InstantSearch>
  );
};

export default ProductsTag;
